import { useEffect, useState } from "react";
import { Loader2, Download, Mail, MessageCircle, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

function downloadCsv(filename, rows) {
  const headers = ["Name", "Phone", "Email", "Region", "Email opt-in", "WhatsApp opt-in", "Orders", "First order", "Last order"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  rows.forEach((c) => {
    lines.push([
      c.name, c.phone, c.email, c.region,
      c.subscribe_email ? "Yes" : "No",
      c.subscribe_whatsapp ? "Yes" : "No",
      c.order_count || 0,
      fmtDate(c.first_order_at), fmtDate(c.last_order_at),
    ].map(esc).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-brand" /></div>;

  const emailSubs = customers.filter((c) => c.subscribe_email);
  const waSubs = customers.filter((c) => c.subscribe_whatsapp);

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="bg-white border border-brand/10 rounded-sm p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-brand-surface flex items-center justify-center text-brand"><Icon className="w-5 h-5" /></div>
      <div><p className="text-2xl font-display text-brand leading-none">{value}</p><p className="text-xs text-brand-muted mt-1">{label}</p></div>
    </div>
  );

  return (
    <div data-testid="admin-customers">
      <div className="grid gap-3 sm:grid-cols-3 mb-5">
        <Stat icon={Users} label="Total customers" value={customers.length} />
        <Stat icon={Mail} label="Email subscribers" value={emailSubs.length} />
        <Stat icon={MessageCircle} label="WhatsApp subscribers" value={waSubs.length} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="export-all" variant="outline" onClick={() => downloadCsv("customers-all.csv", customers)}
          className="rounded-full border-brand/25 text-brand hover:bg-brand hover:text-white gap-1.5"><Download className="w-4 h-4" /> Export all</Button>
        <Button data-testid="export-email" variant="outline" onClick={() => downloadCsv("newsletter-email.csv", emailSubs)}
          className="rounded-full border-brand/25 text-brand hover:bg-brand hover:text-white gap-1.5"><Mail className="w-4 h-4" /> Email subscribers ({emailSubs.length})</Button>
        <Button data-testid="export-whatsapp" variant="outline" onClick={() => downloadCsv("newsletter-whatsapp.csv", waSubs)}
          className="rounded-full border-brand/25 text-brand hover:bg-brand hover:text-white gap-1.5"><MessageCircle className="w-4 h-4" /> WhatsApp subscribers ({waSubs.length})</Button>
      </div>

      {customers.length === 0 ? (
        <p className="text-brand-muted py-10 text-center">No customers yet. They'll appear here automatically as orders come in.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-brand/10 rounded-sm">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="text-left text-brand-muted border-b border-brand/10">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Region</th>
                <th className="px-4 py-3 font-medium text-center">Email</th>
                <th className="px-4 py-3 font-medium text-center">WhatsApp</th>
                <th className="px-4 py-3 font-medium text-center">Orders</th>
                <th className="px-4 py-3 font-medium">Last order</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.customer_id} className="border-b border-brand/5 hover:bg-brand-surface/40" data-testid={`customer-${c.customer_id}`}>
                  <td className="px-4 py-3 text-brand font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-brand-muted">{c.phone}</td>
                  <td className="px-4 py-3 text-brand-muted">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-brand-muted">{c.region}</td>
                  <td className="px-4 py-3 text-center">{c.subscribe_email ? "✅" : "—"}</td>
                  <td className="px-4 py-3 text-center">{c.subscribe_whatsapp ? "✅" : "—"}</td>
                  <td className="px-4 py-3 text-center text-brand">{c.order_count || 1}</td>
                  <td className="px-4 py-3 text-brand-muted">{fmtDate(c.last_order_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
